import { onClickOutside } from '@vueuse/core';
import { onMounted, onUnmounted, type Ref, ref } from 'vue';

type StopHandle = ReturnType<typeof onClickOutside> | null;
type TimeoutId = ReturnType<typeof setTimeout> | null;

type UseDelayedClickOutsideOptions = {
  /** Delay in milliseconds before registering the click outside listener. Defaults to 100ms. */
  delay?: number;
};

/**
 * Composable for handling click-outside detection with a delay.
 * The delay prevents immediate closing when the element is first shown.
 *
 * @param targetRef - Element ref to detect clicks outside of (pass null for document-level)
 * @param callback - Function to call when click outside is detected.
 *                   For document-level detection, receives the MouseEvent.
 *                   For element-level detection (using VueUse), receives the PointerEvent.
 * @param options - Configuration options
 *
 * @example
 * // For element-specific click outside detection (like tooltips)
 * const tooltipRef = useTemplateRef('tooltipRef');
 * useDelayedClickOutside(tooltipRef, () => emit('hide'));
 *
 * @example
 * // For document-level click detection (like menus)
 * useDelayedClickOutside(null, (event) => {
 *   if (!event.target?.closest('[data-menu-container]')) {
 *     closeMenu();
 *   }
 * });
 */
export function useDelayedClickOutside(
  targetRef: Ref<HTMLElement | null> | null,
  callback: (event: MouseEvent | PointerEvent) => void,
  options: UseDelayedClickOutsideOptions = {}
) {
  const { delay = 100 } = options;

  let clickOutsideTimeoutId: TimeoutId = null;
  let stopClickOutside: StopHandle = null;
  const isMounted = ref(false);

  // Handler for document-level click detection
  const handleDocumentClick = (event: MouseEvent) => {
    callback(event);
  };

  onMounted(() => {
    isMounted.value = true;

    // Add a small delay to prevent immediate closing when the element is first shown
    clickOutsideTimeoutId = setTimeout(() => {
      // Only register the listener if component is still mounted
      if (!isMounted.value) return;

      if (targetRef) {
        // Use VueUse's onClickOutside for element-specific detection
        stopClickOutside = onClickOutside(targetRef, callback);
      } else {
        // Use document listener for global click detection
        document.addEventListener('click', handleDocumentClick);
      }

      clickOutsideTimeoutId = null;
    }, delay);
  });

  onUnmounted(() => {
    isMounted.value = false;

    // Clear timeout if it hasn't fired yet
    if (clickOutsideTimeoutId !== null) {
      clearTimeout(clickOutsideTimeoutId);
      clickOutsideTimeoutId = null;
    }

    // Clean up the event listener to prevent memory leaks
    if (stopClickOutside) {
      stopClickOutside();
      stopClickOutside = null;
    } else if (!targetRef) {
      // Remove document listener if we were using document-level detection
      document.removeEventListener('click', handleDocumentClick);
    }
  });

  return {
    isMounted
  };
}
